/**
 * Substep Definitions for Onboarding Wizard
 *
 * Organized by step code. Each substep has:
 * - name: Display name for the substep
 * - code: Unique identifier (format: {step_code}__{substep_code})
 * - description: Brief description of the substep
 * - is_required: Whether this substep is required to be completed before moving on to the next substep
 * - is_deprecated: Set to true to hide from new wizard instances (existing instances unaffected)
 * - data: Additional metadata (nullable)
 * - order: Display order within the step
 * - validation: Optional validation rules for substep completion
 */
import STEP_DEFINITIONS from './stepDefinitions';

const SUBSTEP_DEFINITIONS = {
  [STEP_DEFINITIONS.SUPPORT_AND_CONTACT.code]: {
    SUPPORT_AND_CONTACT: {
      name: 'Support & contact',
      code: `${STEP_DEFINITIONS.SUPPORT_AND_CONTACT.code}__support_and_contact`,
      description: 'Invite key support contacts',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: [],
        allFields: [
          'consultantId',
          'onboardingArchitectId',
          'insideRepresentativeId',
          'region',
          'medbillBuiltInBiller',
          'medbillVirtualOfficeManager',
          'generalNote'
        ]
      }
    }
  },
  [STEP_DEFINITIONS.GO_LIVE_DATES_AND_TRAININGS.code]: {
    GO_LIVE_DATES_AND_TRAININGS: {
      name: 'Go Live & Training Dates',
      code: `${STEP_DEFINITIONS.GO_LIVE_DATES_AND_TRAININGS.code}__go_live_dates_and_trainings`,
      description: 'Set your Practice Go-Live Dates',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['softDate', 'hardDate'],
        allFields: ['softDate', 'hardDate']
      }
    }
  },
  [STEP_DEFINITIONS.ACKNOWLEDGEMENTS.code]: {
    TERMS_AND_CONDITIONS: {
      name: 'Terms & conditions',
      code: `${STEP_DEFINITIONS.ACKNOWLEDGEMENTS.code}__terms_and_conditions`,
      description: 'Terms & conditions',
      is_required: true,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['acknowledge'],
        allFields: ['acknowledge']
      }
    },
    RESPONSIBILITIES: {
      name: 'Responsibilities',
      code: `${STEP_DEFINITIONS.ACKNOWLEDGEMENTS.code}__responsibilities`,
      description: 'Responsibilities',
      is_required: true,
      is_deprecated: false,
      data: null,
      order: 2,
      validation: {
        required: ['acknowledge'],
        allFields: ['acknowledge']
      }
    }
  },
  [STEP_DEFINITIONS.KICKOFF_VIDEO_TRAINING.code]: {
    KICKOFF_VIDEO_TRAINING: {
      name: 'Kickoff video training',
      code: `${STEP_DEFINITIONS.KICKOFF_VIDEO_TRAINING.code}__kickoff_video_training`,
      description: 'Kickoff video training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code]: {
    MAIN_DETAILS: {
      name: 'Main details',
      code: `${STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code}__main_details`,
      description: 'Provide main practice details',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['practiceName', 'taxonomy'],
        allFields: [
          'practiceName',
          'dbaName',
          'taxId',
          'taxIdType',
          'taxonomy',
          'practiceKind',
          'practiceStatus'
        ]
      }
    },
    ADDRESS: {
      name: 'Address',
      code: `${STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code}__address`,
      description: 'Enter your practice location',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2,
      validation: {
        required: ['address1', 'city', 'state', 'zipCode'],
        allFields: ['address1', 'address2', 'city', 'state', 'zipCode']
      }
    },
    CONTACT_INFORMATION: {
      name: 'Contact information',
      code: `${STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code}__contact_information`,
      description: `Enter your practice's information`,
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 3,
      validation: {
        required: ['practiceEmail', 'practicePhone', 'twilioPhone', 'fax'],
        allFields: ['practiceEmail', 'practicePhone', 'twilioPhone', 'fax', 'website']
      }
    },
    BRANDING: {
      name: 'Branding',
      code: `${STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code}__branding`,
      description: 'Setup your practice branding',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 4,
      validation: {
        required: [],
        allFields: []
      }
    },
    ORGANIZATION: {
      name: 'Organization',
      code: `${STEP_DEFINITIONS.BASIC_SETUP_CONFIRMATION.code}__organization`,
      description: 'Add practice to an existing or new organization',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 5,
      validation: {
        required: ['organizationChoice'],
        allFields: ['organizationChoice']
      }
    }
  },
  [STEP_DEFINITIONS.STAFF_AND_PERMISSIONS.code]: {
    ROLES_AND_PERMISSIONS: {
      name: 'Roles & permissions',
      code: `${STEP_DEFINITIONS.STAFF_AND_PERMISSIONS.code}__roles_and_permissions`,
      description: 'Manage or add roles for your staff',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    },
    STAFF_INVITATION: {
      name: 'Staff invitation',
      code: `${STEP_DEFINITIONS.STAFF_AND_PERMISSIONS.code}__staff_invitation`,
      description: 'Invited staff',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2
    }
  },
  [STEP_DEFINITIONS.DELEGATION.code]: {
    DELEGATION: {
      name: 'Delegation',
      code: `${STEP_DEFINITIONS.DELEGATION.code}__delegation`,
      description: 'Delegate tasks to your staff',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.DATA_EXPORT.code]: {
    DATA_EXPORT: {
      name: 'Data export',
      code: `${STEP_DEFINITIONS.DATA_EXPORT.code}__data_export`,
      description: 'Data export',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['dataExportOption'],
        allFields: ['dataExportOption']
      }
    }
  },
  [STEP_DEFINITIONS.KICKOFF_LIVE_TRAINING.code]: {
    KICKOFF_LIVE_TRAINING: {
      name: 'Kickoff live training',
      code: `${STEP_DEFINITIONS.KICKOFF_LIVE_TRAINING.code}__kickoff_live_training`,
      description: 'Kickoff live training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.FRONT_OFFICE_VIDEO_TRAINING.code]: {
    FRONT_OFFICE_VIDEO_TRAINING: {
      name: 'Front office video training',
      code: `${STEP_DEFINITIONS.FRONT_OFFICE_VIDEO_TRAINING.code}__front_office_video_training`,
      description: 'Front office video training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.SCHEDULE_OF_FEES.code]: {
    SCHEDULE_OF_FEES: {
      name: 'Schedule of fees',
      code: `${STEP_DEFINITIONS.SCHEDULE_OF_FEES.code}__schedule_of_fees`,
      description: 'Welcome to Schedule of fees',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.KIOSK_SETUP.code]: {
    KIOSK_SETUP: {
      name: 'Kiosk setup',
      code: `${STEP_DEFINITIONS.KIOSK_SETUP.code}__kiosk_setup`,
      description: 'Kiosk verification code',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['kioskIds'],
        allFields: ['kioskIds']
      }
    }
  },
  [STEP_DEFINITIONS.FRONT_OFFICE_LIVE_TRAINING.code]: {
    FRONT_OFFICE_LIVE_TRAINING: {
      name: 'Front office live training',
      code: `${STEP_DEFINITIONS.FRONT_OFFICE_LIVE_TRAINING.code}__front_office_live_training`,
      description: 'Front office live training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.CLINICAL_VIDEO_TRAINING.code]: {
    CLINICAL_VIDEO_TRAINING: {
      name: 'Clinical video training',
      code: `${STEP_DEFINITIONS.CLINICAL_VIDEO_TRAINING.code}__clinical_video_training`,
      description: 'Clinical video training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.CUSTOM_FORMS.code]: {
    CLINICAL_FORMS: {
      name: 'Clinical forms',
      code: `${STEP_DEFINITIONS.CUSTOM_FORMS.code}__clinical_forms`,
      description: 'Create and customize clinical and patient facing forms',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    },
    FORM_PACKETS: {
      name: 'Form packets',
      code: `${STEP_DEFINITIONS.CUSTOM_FORMS.code}__form_packets`,
      description: 'Create and automate form packets',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2
    }
  },
  [STEP_DEFINITIONS.DATA_IMPORT.code]: {
    ORGANIZATION: {
      name: 'Organization',
      code: `${STEP_DEFINITIONS.DATA_IMPORT.code}__organization`,
      description: 'Import data from an existing organization',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['importChoice'],
        allFields: ['importChoice', 'selectedLocation', 'selectedItems']
      }
    },
    EXTERNAL: {
      name: 'External',
      code: `${STEP_DEFINITIONS.DATA_IMPORT.code}__external`,
      description: 'Start your automatic patient import process',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2,
      validation: {
        required: ['importIds'],
        allFields: ['importIds']
      }
    }
  },
  [STEP_DEFINITIONS.E_PRESCRIBE_SETUP.code]: {
    E_PRESCRIBE_SETUP: {
      name: 'e-Prescribe setup',
      code: `${STEP_DEFINITIONS.E_PRESCRIBE_SETUP.code}__e_prescribe_setup`,
      description: 'E-Prescribe Integration',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: ['setupVeradigm'],
        allFields: ['setupVeradigm']
      }
    }
  },
  [STEP_DEFINITIONS.CLINICAL_LIVE_TRAINING.code]: {
    CLINICAL_LIVE_TRAINING: {
      name: 'Clinical live training',
      code: `${STEP_DEFINITIONS.CLINICAL_LIVE_TRAINING.code}__clinical_live_training`,
      description: 'Clinical live training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.BACK_OFFICE_VIDEO_TRAINING.code]: {
    BACK_OFFICE_VIDEO_TRAINING: {
      name: 'Back office video training',
      code: `${STEP_DEFINITIONS.BACK_OFFICE_VIDEO_TRAINING.code}__back_office_video_training`,
      description: 'Back office video training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.CLEARINGHOUSE.code]: {
    BILLING_PROVIDERS: {
      name: 'Billing providers',
      code: `${STEP_DEFINITIONS.CLEARINGHOUSE.code}__billing_providers`,
      description: 'Manage billing providers',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    },
    FACILITIES: {
      name: 'Facilities',
      code: `${STEP_DEFINITIONS.CLEARINGHOUSE.code}__facilities`,
      description: 'Manage facilities',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2
    },
    PAYERS: {
      name: 'Payers',
      code: `${STEP_DEFINITIONS.CLEARINGHOUSE.code}__payers`,
      description: 'Manage insurance payers',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 3
    },
    EDI_ENROLLMENT: {
      name: 'EDI enrollment',
      code: `${STEP_DEFINITIONS.CLEARINGHOUSE.code}__edi_enrollment`,
      description: 'Add practice to claim MD',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 4,
      validation: {
        required: ['enrollmentIds'],
        allFields: ['enrollmentIds']
      }
    },
    SPECIALITY: {
      name: 'Speciality',
      code: `${STEP_DEFINITIONS.CLEARINGHOUSE.code}__speciality`,
      description: 'Select your practice speciality',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 5,
      validation: {
        required: [],
        allFields: []
      }
    }
  },
  [STEP_DEFINITIONS.HEALTHCARE_PROGRAM.code]: {
    ELIGIBILITY_SETUP: {
      name: 'Eligibility setup',
      code: `${STEP_DEFINITIONS.HEALTHCARE_PROGRAM.code}__eligibility_setup`,
      description: 'Configure the eligibility verification workflow',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1,
      validation: {
        required: [],
        allFields: []
      }
    },
    PAYMENT_PROGRAM: {
      name: 'Payment program',
      code: `${STEP_DEFINITIONS.HEALTHCARE_PROGRAM.code}__payment_program`,
      description: 'Set up how your practice collects payments per care phase',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 2,
      validation: {
        required: [],
        allFields: []
      }
    },
    INCOMEIQUEUE_RCM: {
      name: 'IncomeIQueue RCM',
      code: `${STEP_DEFINITIONS.HEALTHCARE_PROGRAM.code}__incomeiqueue_rcm`,
      description: 'Define automated suggested RCM actions',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 3,
      validation: {
        required: [],
        allFields: []
      }
    }
  },
  [STEP_DEFINITIONS.BACK_OFFICE_LIVE_TRAINING.code]: {
    BACK_OFFICE_LIVE_TRAINING: {
      name: 'Back office live training',
      code: `${STEP_DEFINITIONS.BACK_OFFICE_LIVE_TRAINING.code}__back_office_live_training`,
      description: 'Back office live training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.HARD_LAUNCH_VIDEO_TRAINING.code]: {
    HARD_LAUNCH_VIDEO_TRAINING: {
      name: 'Hard launch video training',
      code: `${STEP_DEFINITIONS.HARD_LAUNCH_VIDEO_TRAINING.code}__hard_launch_video_training`,
      description: 'Hard launch video training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  },
  [STEP_DEFINITIONS.HARD_LAUNCH_LIVE_TRAINING.code]: {
    HARD_LAUNCH_LIVE_TRAINING: {
      name: 'Hard launch live training',
      code: `${STEP_DEFINITIONS.HARD_LAUNCH_LIVE_TRAINING.code}__hard_launch_live_training`,
      description: 'Hard launch live training',
      is_required: false,
      is_deprecated: false,
      data: null,
      order: 1
    }
  }
};

export default SUBSTEP_DEFINITIONS;
